import React from 'react';
import { getBgCharacteristics } from '../../utils/timeline';
import {
  TimelineItemType,
  BELL_SHIELD_TYPES,
  ARROW_ICON_TYPES,
  BORDER_TYPES,
  GRADIENT_TYPES,
  LINE_TYPES,
  isItemTypeForComponent,
} from '../../types/TimelineItemTypes';
import WellbeingFeedbackItem from './WellbeingFeedbackItem';
import WellbeingFeedbackFeatureIconItem from './WellbeingFeedbackFeatureIconItem';
import DotBlueItem from './DotBlueItem';
import DotRedItem from './DotRedItem';
import DotGrayItem from './DotGrayItem';
import BellShieldItem from './BellShieldItem';
import GenericImgItem from './GenericImgItem';
import AllColoursItem from './AllColoursItem';
import AllIconsItem from './AllIconsItem';
import DotBlueSmallItem from './DotBlueSmallItem';
import BorderedItem from './BorderedItem';
import GradientItem from './GradientItem';
import LineMiddleItem from './LineMiddleItem';
import ActionItem from './ActionItem';
import ScriptWrittenMedItem from './ScriptWrittenMedItem';
import MedicationRequestOnlyItem from './MedicationRequestOnlyItem';
import DispenseMhrMedItem from './DispenseMhrMedItem';
import HomeMedItem from './HomeMedItem';
import MedicationRequestHomeItem from './MedicationRequestHomeItem';

interface TimelineItem {
  type: TimelineItemType;
  group: string | number;
  group_type?: string;
  action_status?: string;
  detail?: {
    value?: any;
    homeMedication?: boolean;
  };
  pd_dispense_id?: string | number;
  group_parent_name?: string;
  id: number;
  insight_id: number;
}

type Props = {
  item: TimelineItem;
  getItemProps: any;
  isSimple: boolean;
  openGroups: any;
  groups: any[];
  features: any;
  clinician_id: number;
};

const TimelineItemRenderer: React.FC<Props> = ({
  item,
  getItemProps,
  isSimple,
  openGroups,
  groups,
  features,
  clinician_id,
}) => {
  const itemWidth = parseInt(getItemProps().style.width);
  const itemStyle = getBgCharacteristics(item, itemWidth, clinician_id);

  const isExpanded = !!openGroups[parseInt(item.group.toString())];
  const isParentExpanded = !!(
    groups.find((g) => g.id === item.group) &&
    openGroups[
      parseInt(
        groups.find((g) => g.id === item.group)?.parent ??
          item.group.toString(),
      )
    ]
  );

  // Helper function to check if item type is in a category array
  const isItemTypeInCategoryArray = (category: string[]): boolean => {
    return category.includes(item.type);
  };

  // Render component based on type category
  const renderByType = () => {
    const { type, group_type, action_status } = item;

    // Wellbeing Feedback items (handled by group_type)
    if (group_type === 'Wellbeing Feedback') {
      return (
        <>
          <WellbeingFeedbackItem
            item={item}
            isSimple={isSimple}
            isExpanded={isExpanded}
          />
          {item.detail && item.detail.value !== null && (
            <WellbeingFeedbackFeatureIconItem
              item={item}
              features={features}
              isSimple={isSimple}
              isExpanded={isExpanded}
            />
          )}
        </>
      );
    }

    // Handle specific dot types
    if (type === 'dotBlue' && group_type !== 'Wellbeing Feedback') {
      return (
        <DotBlueItem item={item} isSimple={isSimple} isExpanded={isExpanded} />
      );
    }

    if (type === 'dotRed' && group_type !== 'Wellbeing Feedback') {
      return (
        <DotRedItem item={item} isSimple={isSimple} isExpanded={isExpanded} />
      );
    }

    if (type === 'dotGray' && group_type !== 'Wellbeing Feedback') {
      return (
        <DotGrayItem item={item} isSimple={isSimple} isExpanded={isExpanded} />
      );
    }

    // Action items
    if (type === 'action') {
      return (
        <ActionItem
          item={item}
          clinician_id={clinician_id}
          isSimple={isSimple}
          isExpanded={isExpanded}
        />
      );
    }

    // Bell and Shield items (excluding inactive actions)
    if (isItemTypeInCategoryArray(BELL_SHIELD_TYPES) && action_status !== 'I') {
      return (
        <BellShieldItem
          item={item}
          isSimple={isSimple}
          isExpanded={isExpanded}
        />
      );
    }

    // Generic image items
    if (isItemTypeInCategoryArray(ARROW_ICON_TYPES)) {
      return (
        <GenericImgItem
          item={item}
          isSimple={isSimple}
          isExpanded={isExpanded}
        />
      );
    }

    // All colours items (including prescription)
    if (
      isItemTypeInCategoryArray([
        'dotDarkBlue',
        'dotGrey',
        'dotDiagnostic',
        'dotPurple',
        'prescription',
        'automedicPrescription',
        'dotMedProfile',
      ])
    ) {
      return (
        <AllColoursItem
          item={item}
          isSimple={isSimple}
          isExpanded={isExpanded || isParentExpanded}
        />
      );
    }

    // All icons
    if (type === 'allIcons') {
      return (
        <AllIconsItem item={item} isSimple={isSimple} isExpanded={isExpanded} />
      );
    }

    // Small blue dot
    if (type === 'dotBlueSmall') {
      return (
        <DotBlueSmallItem
          item={item}
          isSimple={isSimple}
          isExpanded={isExpanded}
        />
      );
    }

    // Bordered items
    if (isItemTypeInCategoryArray(BORDER_TYPES)) {
      return (
        <BorderedItem item={item} isSimple={isSimple} isExpanded={isExpanded} />
      );
    }

    // Gradient items
    if (isItemTypeInCategoryArray(GRADIENT_TYPES)) {
      return (
        <GradientItem item={item} isSimple={isSimple} isExpanded={isExpanded} />
      );
    }

    // Line items
    if (isItemTypeInCategoryArray(LINE_TYPES)) {
      return (
        <LineMiddleItem
          item={item}
          isSimple={isSimple}
          isExpanded={isExpanded || isParentExpanded}
        />
      );
    }

    if (isItemTypeForComponent(type, 'ScriptWrittenMedItem')) {
      return (
        <ScriptWrittenMedItem
          item={item}
          isSimple={isSimple}
          isExpanded={isExpanded || isParentExpanded}
        />
      );
    }

    if (isItemTypeForComponent(type, 'MedicationRequestOnlyItem')) {
      return <MedicationRequestOnlyItem item={item} />;
    }

    if (isItemTypeForComponent(type, 'MedicationRequestHomeItem')) {
      return <MedicationRequestHomeItem item={item} />;
    }

    if (isItemTypeForComponent(type, 'DispenseMhrMedItem')) {
      return <DispenseMhrMedItem item={item} />;
    }

    if (isItemTypeForComponent(type, 'HomeMedItem')) {
      return <HomeMedItem item={item} />;
    }

    // Default fallback - should not happen with proper typing
    return null;
  };

  return (
    <div
      {...getItemProps({
        style: {
          ...itemStyle,
          border: 'none !important',
          outline: 'none !important',
        },
      })}
    >
      {renderByType()}
    </div>
  );
};

export default TimelineItemRenderer;
